import { ChangeEvent, FormEvent, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Form } from "react-bootstrap";
import { AdminFormPage, saveErrorToast } from "@components/CatalogIndex";
import type { AdminFormToast } from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import TextField from "@components/FormsV2/TextField/TextField";
import MultipleLineFieldV2 from "@components/FormsV2/MultipleLineFieldV2/MultipleLineFieldV2";
import { useV2GetProjectByIdQuery } from "@generated";
import { projectChromeQueryArgs } from "@components/SowHeader/projectChromeQuery";
import useChangeOrderAccess from "../hooks/useChangeOrderAccess";
import { useChangeOrderMutations } from "../api";
import { changeOrderChild } from "../utils/changeOrderChild";
import useBuilderData from "../builder/useBuilderData";
import useChangeOrderPreview from "../builder/useChangeOrderPreview";
import { useChangeOrderDraft, validateDraft } from "../builder/useChangeOrderDraft";
import type { DraftErrors } from "../builder/useChangeOrderDraft";
import { buildCreateBody, buildPreviewBody } from "../builder/payload";
import ChangeScopeGrid from "../builder/ChangeScopeGrid";
import ChangeOrderGovernance from "../builder/ChangeOrderGovernance";
import SummaryBar from "../builder/SummaryBar";
import TypeSwitch from "../builder/TypeSwitch";
import CauseRadios from "../builder/CauseRadios";
import type { Totals } from "../builder/types";
import "../builder/builder.css";

interface Props {
  projectId: string;
}

const FORM_ID = "change-order-form";

const New = ({ projectId }: Props) => {
  const navigate = useNavigate();
  const access = useChangeOrderAccess();
  const { createFromBody } = useChangeOrderMutations();
  const [draft, dispatch] = useChangeOrderDraft();
  const [errors, setErrors] = useState<DraftErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<AdminFormToast | null>(null);

  // Shares the project chrome's cache entry.
  const { data: projectData } = useV2GetProjectByIdQuery(projectChromeQueryArgs(projectId));
  const project = projectData?.data;
  const currency = (project?.attributes as any)?.["field-labels"]?.currency_unit ?? "$";
  const data = useBuilderData(projectId);

  const previewBody = useMemo(
    () => buildPreviewBody(draft, data.lines, projectId),
    [draft, data.lines, projectId]
  );
  const {
    preview: lastPreview,
    error: previewError,
    errorDetail: previewErrorDetail,
  } = useChangeOrderPreview(data.isLoading ? null : previewBody);
  // A failing preview's last figures no longer match the draft.
  const preview = previewError ? null : lastPreview;

  // A change-request project can't have change requests of its own.
  if (!access.canCreate || changeOrderChild(project).isChangeOrder) {
    return <Navigate to=".." replace />;
  }

  const setField = (
    field: "causeNote" | "externalReference" | "externalUrl" | "trackedHours" | "trackedRevenue",
    value: string
  ) => {
    dispatch({ type: "setField", field, value });
    if (field in errors) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const tracked = draft.mode === "tracked";
  // Scope mode: only the preview's parent figures (live effort plus
  // governance, matching how the change's hours are counted), dashes until the
  // first pass. Tracked mode has no preview, so it falls back to the project's
  // totals.
  const attributes = (project?.attributes ?? {}) as Record<string, unknown>;
  const projectTotals: Totals | null = project
    ? {
        hours: Number(attributes["total-effort"]) || 0,
        revenue: Number(attributes["contract-revenue"]) || 0,
        cost: Number(attributes["contract-cost"]) || 0,
      }
    : null;
  const original = lastPreview?.parent ?? (tracked ? projectTotals : null);
  const trackedChange: Totals | null = tracked
    ? {
        hours: Number(draft.trackedHours) || 0,
        revenue: Number(draft.trackedRevenue) || 0,
        cost: 0,
      }
    : null;

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const found = validateDraft(draft, data.lines);
    setErrors(found);
    if (Object.values(found).some(Boolean)) return;
    setSubmitting(true);
    try {
      const created = await createFromBody(buildCreateBody(draft, data.lines, projectId));
      navigate(`../${created.id}`, {
        state: { toast: { variant: "success", text: `${created.label} created.` } },
      });
    } catch (err) {
      console.error("Failed to create change order", err);
      setToast(saveErrorToast(err, "change request", "add"));
      setSubmitting(false);
    }
  };

  return (
    <>
      <AdminFormPage
        title="New Change Request"
        formId={FORM_ID}
        onCancel={() => navigate("..")}
        isSubmitting={submitting}
      >
        <div className="changeOrderBuilder">
          <SummaryBar
            original={original}
            change={tracked ? trackedChange : preview?.totals ?? null}
            currency={currency}
            showMargin={!tracked}
          />
          {!tracked && previewError && (
            <Form.Text className="text-danger mb-2 d-block" role="alert">
              Couldn&apos;t calculate totals
              {previewErrorDetail ? `: ${previewErrorDetail}` : ""}
            </Form.Text>
          )}
          <Form id={FORM_ID} data-testid={FORM_ID} onSubmit={handleSubmit} noValidate>
            <TypeSwitch
              value={draft.mode}
              onChange={(mode) => dispatch({ type: "setMode", mode })}
            />
            <CauseRadios
              id={FORM_ID}
              value={draft.cause}
              error={errors.cause}
              onChange={(cause) => {
                dispatch({ type: "setField", field: "cause", value: cause });
                setErrors((prev) => ({ ...prev, cause: undefined }));
              }}
            />
            <div className="row">
              <div className="col-sm-12">
                <Form.Label htmlFor={`${FORM_ID}-cause-note`}>Note</Form.Label>
                <MultipleLineFieldV2
                  label={null}
                  id={`${FORM_ID}-cause-note`}
                  rows={3}
                  value={draft.causeNote}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setField("causeNote", e.target.value)}
                />
              </div>
            </div>
            <div className="row mb-3">
              <div className="col-sm-6">
                <Form.Label htmlFor={`${FORM_ID}-external-reference`}>External Reference</Form.Label>
                <TextField
                  label={null}
                  id={`${FORM_ID}-external-reference`}
                  value={draft.externalReference}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setField("externalReference", e.target.value)}
                />
              </div>
              <div className="col-sm-6">
                <Form.Label htmlFor={`${FORM_ID}-external-url`}>External URL</Form.Label>
                <TextField
                  label={null}
                  id={`${FORM_ID}-external-url`}
                  value={draft.externalUrl}
                  placeholder="https://"
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setField("externalUrl", e.target.value)}
                />
                {errors.externalUrl && <Form.Text className="text-danger">{errors.externalUrl}</Form.Text>}
              </div>
            </div>

            {tracked ? (
              <div className="row">
                <div className="col-sm-12">
                  <Form.Text className="text-muted mb-2 d-block">
                    The work is scoped and approved in your PSA. Record its hours and revenue here for reference.
                  </Form.Text>
                </div>
                <div className="col-sm-3">
                  <Form.Label htmlFor={`${FORM_ID}-hours`}>Hours</Form.Label>
                  <Form.Control
                    id={`${FORM_ID}-hours`}
                    type="number"
                    min={0}
                    step="any"
                    value={draft.trackedHours}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setField("trackedHours", e.target.value)}
                  />
                  {errors.trackedHours && <Form.Text className="text-danger">{errors.trackedHours}</Form.Text>}
                </div>
                <div className="col-sm-3">
                  <Form.Label htmlFor={`${FORM_ID}-revenue`}>Revenue</Form.Label>
                  <Form.Control
                    id={`${FORM_ID}-revenue`}
                    type="number"
                    min={0}
                    step="any"
                    value={draft.trackedRevenue}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setField("trackedRevenue", e.target.value)}
                  />
                  {errors.trackedRevenue && <Form.Text className="text-danger">{errors.trackedRevenue}</Form.Text>}
                </div>
                <div className="col-sm-12">
                  <Form.Text className="text-muted">Your service changes are kept if you switch back.</Form.Text>
                </div>
              </div>
            ) : data.isLoading ? (
              <ScopeStackSpinner />
            ) : data.error ? (
              <Form.Text className="text-danger" role="alert">
                Couldn&apos;t load this project&apos;s services. You can still save a change request tracked in another system.
              </Form.Text>
            ) : (
              <>
                {errors.lines && (
                  <Form.Text className="text-danger mb-2 d-block" role="alert">
                    {errors.lines}
                  </Form.Text>
                )}
                {(
                  [
                    ["Professional Services", data.professional],
                    ["Managed Services", data.managed],
                  ] as const
                ).map(([title, services]) => (
                  <ChangeScopeGrid
                    key={title}
                    title={title}
                    services={services}
                    revised={draft.revised}
                    onRevise={(key, values) => {
                      dispatch({ type: "reviseLine", key, values });
                      if (errors.lines) setErrors((prev) => ({ ...prev, lines: undefined }));
                    }}
                    resourceOptions={data.resourceOptions}
                    previewLines={preview?.lines ?? null}
                    currency={currency}
                  />
                ))}
                <ChangeOrderGovernance
                  governances={data.governances}
                  preview={preview?.governances ?? null}
                  overrides={draft.governance}
                  onOverride={(id, hours) => dispatch({ type: "setGovernance", id, hours })}
                  currency={currency}
                />
              </>
            )}
          </Form>
        </div>
      </AdminFormPage>
      {toast && (
        <ToastAlert variant={toast.variant} text={toast.text} onClose={() => setToast(null)} />
      )}
    </>
  );
};

export default New;
